import React from 'react';
import Router from 'next/router';
import { GenreNameToKey } from '@soundxyz/utils/const';
import { theme } from '@components-shared/src/stitches';
import { ParamsStore } from '@context/params';
import { NAV_LINKS } from '@utils/constants';
import { scrollToTop } from '@utils/helpers';
import { resetFilters, resetParams } from '../helpers';
import type { ExploreTypeChoice } from '../schema';
import { EXPLORE_TYPES } from '../schema';
import { setDefaultSort, setExploreType } from '../useSearchPageStore';
import { ChartCard } from './ChartCard';
import { ExploreCard } from './ExploreCard';
import { GenreCard } from './GenreCard';
import { GENRE_DATA } from './data';

export const useGetExploreCards = () => {
  const onExploreCardClick = ({ type }: { type: ExploreTypeChoice }) => {
    if (type === EXPLORE_TYPES.UPCOMING_DROPS) {
      Router.push(NAV_LINKS.Upcoming);
    } else {
      Router.push(NAV_LINKS.Explore).then(() => {
        setExploreType({ type });
        resetFilters();
        resetParams();
        scrollToTop();
        setDefaultSort({ type });
      });
    }
  };

  return [
    <ExploreCard
      key="upcomingDrops"
      label="Upcoming Drops"
      className="bg-green300"
      onClick={() => onExploreCardClick({ type: EXPLORE_TYPES.UPCOMING_DROPS })}
      backgroundColor=""
    />,
    <ExploreCard
      key="sounds"
      label="Sounds"
      backgroundColor={theme.colors.userEducation600.toString()}
      onClick={() => onExploreCardClick({ type: EXPLORE_TYPES.SOUNDS })}
    />,
    <ExploreCard
      key="artists"
      label="Artists"
      backgroundColor={theme.colors.accent700.toString()}
      onClick={() => onExploreCardClick({ type: EXPLORE_TYPES.ARTISTS })}
    />,
    <ExploreCard
      key="playlists"
      label="Playlists"
      backgroundColor={theme.colors.brand600.toString()}
      onClick={() => onExploreCardClick({ type: EXPLORE_TYPES.PLAYLISTS })}
    />,
    <ExploreCard
      key="collectors"
      label="Collectors"
      backgroundColor={theme.colors.base600.toString()}
      onClick={() => onExploreCardClick({ type: EXPLORE_TYPES.COLLECTORS })}
    />,
  ];
};

export const useGetGenreCards = () => {
  return GENRE_DATA.map((data, index) => {
    const IS_PRIORITY_IMAGE = index < 6 ? true : false;

    return (
      <GenreCard
        key={index}
        label={data.label}
        backgroundColor={data.backgroundColor}
        imgUrl={data.imgUrl}
        isPriorityImage={IS_PRIORITY_IMAGE}
        onClick={() => {
          scrollToTop();
          setExploreType({ type: EXPLORE_TYPES.SOUNDS });
          setDefaultSort({ type: EXPLORE_TYPES.SOUNDS });
          ParamsStore.genre = GenreNameToKey[data.key];
        }}
      />
    );
  });
};

export const useChartCards = () => {
  return [
    <ChartCard
      key="viralSounds"
      label="Viral Sounds"
      pathnameUri={NAV_LINKS.ViralChartsSounds}
      className="bg-education200"
    />,
    <ChartCard
      key="viralPlaylists"
      label="Viral Playlists"
      pathnameUri={NAV_LINKS.ViralChartsPlaylists}
      className="bg-brand500"
    />,
    <ChartCard
      key="topSongs"
      label="Top Songs"
      pathnameUri={NAV_LINKS.TopSongs}
      className="bg-orange100"
    />,
    <ChartCard
      key="topArtists"
      label="Top Artists"
      pathnameUri={NAV_LINKS.TopArtists}
      className="bg-fuchsia500"
    />,
    <ChartCard
      key="topCollectors"
      label="Top Collectors"
      pathnameUri={NAV_LINKS.TopCollectors}
      className="bg-base600"
    />,
    <ChartCard
      key="topCurators"
      label="Top Curators"
      pathnameUri={NAV_LINKS.TopCurators}
      className="bg-destructive100"
    />,
  ];
};
